import styled, { keyframes } from 'styled-components';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

export const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 6rem 0 4rem;
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 3rem;
    padding-top: 2rem;
    text-align: center;
  }
`;

export const HeroTextBlock = styled.div`
  animation: ${fadeUp} 0.8s ease-out;
`;

export const HeroGreeting = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: ${({ theme }) => theme.fonts.weight.medium};
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
`;

export const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  color: ${({ theme }) => theme.colors.heading};
  line-height: 1.15;
  margin-bottom: 1.5rem;
  letter-spacing: -0.03em;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const TypewriterWrapper = styled.span`
  position: relative;
  display: inline-block;
  vertical-align: top;
  max-width: 100%;
`;

export const TypewriterGhost = styled.span`
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    white-space: normal;
  }
`;

export const TypewriterText = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.accent_gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    white-space: normal;
  }
`;

export const Cursor = styled.span`
  display: inline-block;
  width: 3px;
  height: 1em;
  background: ${({ theme }) => theme.colors.accent};
  margin-left: 4px;
  vertical-align: text-bottom;
  animation: ${blink} 0.8s step-end infinite;
`;

export const HeroDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.para};
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 520px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 100%;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

export const SocialIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.glass_border};
  color: ${({ theme }) => theme.colors.para};
  font-size: 1.15rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.bg_card};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.glass_border_hover};
    background: rgba(59, 130, 246, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.15);
  }
`;

export const HeroImageBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  animation: ${fadeUp} 1s ease-out 0.3s both;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 340px;
  height: 420px;
  border-radius: ${({ theme }) => theme.radius.xl};
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.border_light};
  box-shadow:
    0 25px 60px ${({ theme }) => theme.colors.shadow},
    0 0 80px rgba(59, 130, 246, 0.08);
  z-index: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }

  /* Gradient fade at bottom to blend light photo bg into dark theme */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${({ theme }) => theme.colors.bg_primary}cc 70%,
      ${({ theme }) => theme.colors.bg_primary} 100%
    );
    pointer-events: none;
  }

  /* Subtle accent border glow */
  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    background: ${({ theme }) => theme.colors.accent_gradient};
    z-index: -1;
    opacity: 0.3;
    transition: ${({ theme }) => theme.transitions.default};
  }

  &:hover::before {
    opacity: 0.5;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 260px;
    height: 320px;
  }
`;

export const HeroGlow = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.12),
    transparent 70%
  );
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
`;

export const FloatingParticle = styled.img`
  position: absolute;
  width: 40px;
  height: 40px;
  opacity: 0.4;
  animation: ${float} ${({ duration }) => duration || '6s'} ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || '0s'};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  pointer-events: none;
  z-index: 1;
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(
      ellipse at 20% 50%,
      rgba(59, 130, 246, 0.08) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 80% 20%,
      rgba(139, 92, 246, 0.06) 0%,
      transparent 50%
    );
  pointer-events: none;
  z-index: 0;
`;
